/**
 * The `Requests` section's row: who asked, when, and the `Approve` and
 * `Decline` controls. It decides nothing; `isBusy` scopes the pending state
 * to this row alone, per `.agents/rules/scoped-loading-state.mdc`.
 */
import { Button } from "@sportsmet/ui";
import { type ReactNode } from "react";

import type { typeRosterRequestRow } from "./membersRosterModel";

export type typePoolMemberRequestRowProps = {
  row: typeRosterRequestRow;
  isBusy: boolean;
  onApprove: (userId: string) => void;
  onDecline: (userId: string) => void;
};

export const APPROVE_LABEL = "Approve";
export const DECLINE_LABEL = "Decline";

export const PoolMemberRequestRow = ({
  row,
  isBusy,
  onApprove,
  onDecline,
}: typePoolMemberRequestRowProps): ReactNode => {
  return (
    <li
      className="flex items-center justify-between gap-3"
      data-testid="pool-member-request-row"
      data-user-id={row.userId}
    >
      <div className="flex min-w-0 flex-1 items-center gap-3">
        {row.avatarUrl !== null && row.avatarUrl !== "" ? (
          <img
            src={row.avatarUrl}
            alt=""
            className="size-8 shrink-0 rounded-full object-cover"
          />
        ) : (
          <span
            aria-hidden="true"
            className="grid size-8 shrink-0 place-items-center rounded-full bg-ink-secondary text-caption font-bold text-ink-inverse"
          >
            {row.initials}
          </span>
        )}
        <div className="flex min-w-0 flex-col">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-body-strong text-ink">{row.displayName}</span>
            {row.usernameLabel === null ? null : (
              <span className="text-caption text-ink-secondary">
                {row.usernameLabel}
              </span>
            )}
          </div>
          <span className="text-caption text-ink-secondary">
            {row.askedLabel}
          </span>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Button
          variant="secondary"
          size="md"
          data-testid="pool-member-decline"
          loading={isBusy}
          disabled={isBusy}
          onClick={() => {
            onDecline(row.userId);
          }}
        >
          {DECLINE_LABEL}
        </Button>
        <Button
          variant="primary"
          size="md"
          data-testid="pool-member-approve"
          loading={isBusy}
          disabled={isBusy}
          onClick={() => {
            onApprove(row.userId);
          }}
        >
          {APPROVE_LABEL}
        </Button>
      </div>
    </li>
  );
};
